import type { RollupAggregation, RollupCellRawValue } from './define';

/**
 * Computes a rollup value from source values using the specified aggregation
 */
export function computeRollupValue(
  sourceValues: unknown[],
  aggregation: RollupAggregation,
  separator?: string
): RollupCellRawValue {
  // Filter null/undefined values
  const values = sourceValues.filter(v => v != null && v !== '');
  const totalCount = sourceValues.length;

  switch (aggregation) {
    case 'count':
      return totalCount;

    case 'count_values':
      return values.length;

    case 'count_unique':
      return new Set(values.map(String)).size;

    case 'sum':
      return values.reduce((acc: number, v) => {
        const num = Number(v);
        return acc + (isNaN(num) ? 0 : num);
      }, 0);

    case 'average': {
      const numericValues = values.map(v => Number(v)).filter(n => !isNaN(n));
      if (numericValues.length === 0) return 0;
      const sum = numericValues.reduce((acc, v) => acc + v, 0);
      return sum / numericValues.length;
    }

    case 'min': {
      const numericValues = values.map(v => Number(v)).filter(n => !isNaN(n));
      if (numericValues.length === 0) return null;
      return Math.min(...numericValues);
    }

    case 'max': {
      const numericValues = values.map(v => Number(v)).filter(n => !isNaN(n));
      if (numericValues.length === 0) return null;
      return Math.max(...numericValues);
    }

    case 'show_original':
      return values.map(String);

    case 'show_unique':
      return [...new Set(values.map(String))];

    case 'concatenate':
      return values.map(String).join(separator ?? ', ');

    case 'percent_empty':
      if (totalCount === 0) return 0;
      return Math.round(((totalCount - values.length) / totalCount) * 100);

    case 'percent_filled':
      if (totalCount === 0) return 0;
      return Math.round((values.length / totalCount) * 100);

    default:
      return null;
  }
}

/**
 * Metadata about aggregation functions for UI
 */
export const AGGREGATION_META: Record<
  RollupAggregation,
  {
    label: string;
    icon: string;
    applicableTo: ('number' | 'string' | 'date' | 'all')[];
  }
> = {
  show_original: {
    label: 'Show original',
    icon: '',
    applicableTo: ['all'],
  },
  show_unique: {
    label: 'Show unique',
    icon: '',
    applicableTo: ['all'],
  },
  count: {
    label: 'Count all',
    icon: '',
    applicableTo: ['all'],
  },
  count_values: {
    label: 'Count values',
    icon: '',
    applicableTo: ['all'],
  },
  count_unique: {
    label: 'Count unique',
    icon: '',
    applicableTo: ['all'],
  },
  sum: {
    label: 'Sum',
    icon: '',
    applicableTo: ['number'],
  },
  average: {
    label: 'Average',
    icon: '',
    applicableTo: ['number'],
  },
  min: {
    label: 'Min',
    icon: '',
    applicableTo: ['number', 'date'],
  },
  max: {
    label: 'Max',
    icon: '',
    applicableTo: ['number', 'date'],
  },
  percent_empty: {
    label: '% empty',
    icon: '',
    applicableTo: ['all'],
  },
  percent_filled: {
    label: '% filled',
    icon: '',
    applicableTo: ['all'],
  },
  concatenate: {
    label: 'Concatenate',
    icon: '',
    applicableTo: ['string'],
  },
};
